import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
// CORE
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { categoriesSelectors } from 'app-components/flux/categories';
import { tagsSelectors } from 'app-components/flux/tags';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

// COMPONENTS
import QResourcePicker from 'components/QResourcePicker';
import QTip from 'components/QuickenControls/QTip';

import { getNameFromKeyFactory } from 'components/QFilterSelect/utils';
// DATA & UTILS
import { createFilterData } from 'containers/SpendingWatchlist/utils';

// LOCAL
import { payeesSelectors } from 'app-components/flux/payees';
import ChipList from '../ChipList';

const useStyles = makeStyles()(({ _palette }) => ({
  root: {
    position: 'relative',
    minWidth: 78,
    marginTop: 0,
    outline: 'none',
    '& *': {
      cursor: 'pointer !important',
    },
  },
  minHeight: {
    minHeight: 40,
  },
  fab: {
    zIndex: 2,
    minHeight: 32,
    height: 40,
    width: 40,
  },

  // popover internal styles bellow here
  popoverPaper: {
    maxHeight: 500,
    overflowY: 'auto',
    width: 410,
    height: 560,
    padding: 10,
    display: 'flex',
    transform: 'translateY(-27px) !important',
  },
  chipContainerWithScroll: {
    margin: 0,
    maxHeight: 130,
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 16,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
  },
  textFieldMulti: {
    maxWidth: 450,
    width: 410,
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: 12,
  },
}));

const convertFilterToSelectedItems = (filter) => {
  switch (filter.type) {
    case 'PAYEE': {
      return filter.payees ? ImmutableSet(filter.payees) : ImmutableSet();
    }
    case 'TAG':
      return filter.tags ? ImmutableSet(filter.tags.map(({ id }) => id)) : ImmutableSet();
    case 'CHARTOFACCOUNT':
      return filter.chartOfAccounts ? ImmutableSet(filter.chartOfAccounts.map(({ id }) => id)) : ImmutableSet();
    default:
      return new ImmutableSet();
  }
};

const filterResources = (item) => !item.id?.includes('BALANCE_ADJUSTMENT');

const getInputProps = (classes, popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus) => ({
  autoComplete: 'off',
  inputComponent: ChipList,
  inputProps: {
    id,
    chips: collapsedChipList,
    maxChips: 100,
    onRemoveChip: handleRemoveChip,
    expandable: false,
    zeroDiv: true,
    showChips: popoverEl === null,
    endAdornment: true,
    onBlur,
    onFocus,
  },
});

const TypeSelectField = (props) => {
  const { id, error, helperText, onBlur: onDismiss, onChange, itemsType, filter, filterTypeKey, label } = props;
  const [popoverEl, setPopoverEl] = useState(null);
  const { classes } = useStyles({ popoverEl });

  const selectRef = useRef();
  const [focused, setFocus] = useState(null);

  const categoriesById = useSelector(categoriesSelectors.getCategoriesById);
  const payeesByName = useSelector(payeesSelectors.getPayeesForTransactions);
  const tagsById = useSelector(tagsSelectors.getTags);
  const [hasBeenHydrated, setHasBeenHydrated] = useState(false);

  const [selectedItems, setSelectedItems] = useState(ImmutableSet());

  useEffect(() => {
    if (!hasBeenHydrated && filter) {
      const initialSelectedItems = convertFilterToSelectedItems(filter);
      setSelectedItems(initialSelectedItems);
      setHasBeenHydrated(true);
    }
  }, [filterTypeKey, hasBeenHydrated, filter]);

  useEffect(() => {
    setHasBeenHydrated(false);
  }, [filterTypeKey]);

  const onOpenPicker = useCallback(() => {
    setPopoverEl(selectRef.current);
  }, [setPopoverEl, selectRef]);

  const onClosePopover = useCallback(() => {
    onDismiss?.();
    setPopoverEl(null);
    setTimeout(() => selectRef.current?.focus(), 0);
  }, [setPopoverEl, onDismiss]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getNameForItem = useCallback(getNameFromKeyFactory(categoriesById, payeesByName, tagsById, null), [
    categoriesById,
    payeesByName,
    tagsById,
  ]);

  const getNameOfFirstItem = useCallback(
    (firstItemId) => {
      if (itemsType === 'categories' && firstItemId) {
        const parent = categoriesById.get(firstItemId)?.parentId;
        return getNameForItem('categories', parent !== '0' ? parent : firstItemId);
      }
      if (itemsType === 'payeeNames' && firstItemId) {
        return getNameForItem('payees', firstItemId);
      }
      if (itemsType === 'tags' && firstItemId) {
        return getNameForItem('tags', firstItemId);
      }
      return '';
    },
    [itemsType, getNameForItem, categoriesById],
  );

  const handleAddChip = useCallback(
    (newSet) => {
      setSelectedItems(newSet);
      let filteredData;
      switch (itemsType) {
        case 'payeeNames':
          filteredData = newSet.map((item) => getNameForItem('payees', item));
          break;
        case 'categories':
          // now that we are adding usage to category selection for business, make sure we filter it out
          filteredData = newSet.filter((item) => !item.includes('USAGE'));
          break;
        default:
          filteredData = newSet;
          break;
      }
      const newFilterData = createFilterData(itemsType, filteredData);

      onChange(newFilterData, getNameOfFirstItem(newSet.toArray()[0]));
    },
    [itemsType, getNameForItem, onChange, getNameOfFirstItem],
  );

  const handleRemoveChip = useCallback(
    (removedItem) => {
      let updatedSet = selectedItems.delete(removedItem.id);
      if (itemsType === 'categories') {
        const childIds =
          categoriesSelectors.getChildCategoriesByParentIdUtil(categoriesById, removedItem.id).keySeq().toArray() || [];
        if (childIds.length > 0) updatedSet = updatedSet.subtract(childIds);
      }
      setSelectedItems(updatedSet);
      const newFilterData = createFilterData(
        itemsType,
        itemsType === 'payeeNames' ? updatedSet.map((item) => getNameForItem('payees', item)) : updatedSet,
      );

      onChange(newFilterData, getNameOfFirstItem(updatedSet.toArray()[0]));
    },
    [selectedItems, itemsType, categoriesById, getNameForItem, onChange, getNameOfFirstItem],
  );

  const collapsedChipList = useMemo(() => {
    let retList = ImmutableList();
    if (itemsType === 'categories') {
      retList = selectedItems
        .map((catId) => ({
          id: catId,
          name: getNameForItem('categories', catId),
          parentId: categoriesById.get(catId)?.parentId,
        }))
        .filter((item) => !item.parentId || !selectedItems.has(item.parentId));
    } else if (itemsType === 'payeeNames') {
      retList = selectedItems.map((payeeId) => ({ id: payeeId, name: getNameForItem('payees', payeeId) }));
    } else if (itemsType === 'tags') {
      retList = selectedItems.map((tagId) => ({ id: tagId, name: getNameForItem('tags', tagId) }));
    }
    return retList.toArray();
  }, [selectedItems, getNameForItem, itemsType, categoriesById]);

  const onTextFieldClick = useCallback(() => {
    if (!popoverEl) {
      onOpenPicker();
    }
  }, [popoverEl, onOpenPicker]);

  const onBlur = useCallback(() => {
    setFocus(false);
  }, []);
  const onFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const inputProps = useMemo(
    () => getInputProps(classes, popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus),
    [classes, popoverEl, collapsedChipList, handleRemoveChip, id, onBlur, onFocus],
  );

  return (
    <div className={classes.root}>
      <TextField
        inputRef={selectRef}
        label={label}
        value={collapsedChipList}
        multiline
        error={error}
        helperText={helperText}
        InputProps={inputProps}
        onClick={onTextFieldClick}
        className={classes.textFieldMulti}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onTextFieldClick();
          }
        }}
        variant='filled'
        margin={'normal'}
        focused={Boolean(popoverEl) || document.activeElement === selectRef?.current || focused}
      ></TextField>
      <Popover
        id='type-select-picker-popover'
        open={Boolean(popoverEl)}
        anchorEl={popoverEl}
        onClose={onClosePopover}
        placement={'top-start'}
        classes={{
          paper: classes.popoverPaper,
        }}
      >
        <QResourcePicker
          autoFocus
          noHeader
          selectedItems={selectedItems}
          onSelect={handleAddChip}
          resourceType={itemsType}
          pageSize={12}
          filterFn={filterResources}
          noBorder
          noSpecial
        >
          {collapsedChipList.length > 0 ? (
            <Box display={'flex'} margin={'4px 0'}>
              <ChipList chips={collapsedChipList} maxChips={5} onRemoveChip={handleRemoveChip} zeroDiv={false} />
              <div className={classes.chipContainerWithScroll}>
                <QTip title='Done' placement='top' distance={0}>
                  <Fab
                    color='primary'
                    aria-label='close-chooser'
                    id='close-chooser'
                    className={classes.fab}
                    onClick={onClosePopover}
                    size='small'
                  >
                    <CheckIcon className={classes.minHeight} />
                  </Fab>
                </QTip>
              </div>
            </Box>
          ) : null}
        </QResourcePicker>
      </Popover>
    </div>
  );
};

TypeSelectField.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  itemsType: PropTypes.string,
  filter: PropTypes.object,
  filterTypeKey: PropTypes.string,
  label: PropTypes.string,
  onBlur: PropTypes.func, // Added onBlur to propTypes validation
};

TypeSelectField.whyDidYouRender = true;

export default TypeSelectField;
